import * as React from "react";
import { Check, Circle, CircleDot } from "lucide-react";
import { cn } from "../../lib/utils";
import type { StatusTone } from "../../design/tokens";

export type TimelineStatus = "complete" | "current" | "upcoming" | "blocked";

const statusMeta: Record<
  TimelineStatus,
  { tone: StatusTone; icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  complete: { tone: "success", icon: Check, label: "Completed" },
  current: { tone: "info", icon: CircleDot, label: "In progress" },
  upcoming: { tone: "neutral", icon: Circle, label: "Not started" },
  blocked: { tone: "warning", icon: Circle, label: "Blocked" },
};

const markerStyles: Record<StatusTone, string> = {
  neutral: "border-tone-neutral-border bg-background text-tone-neutral-solid",
  info: "border-tone-info-border bg-tone-info-surface text-tone-info-foreground",
  success:
    "border-tone-success-border bg-tone-success-surface text-tone-success-foreground",
  warning:
    "border-tone-warning-border bg-tone-warning-surface text-tone-warning-foreground",
  danger:
    "border-tone-danger-border bg-tone-danger-surface text-tone-danger-foreground",
};

export type TimelineProps = {
  /** Names the sequence, e.g. "Relocation milestones". */
  label: string;
  children: React.ReactNode;
  className?: string;
};

/**
 * An ordered sequence of milestones. It is a real ordered list so the number of
 * steps and the position within them are announced without extra markup.
 */
export function Timeline({ label, children, className }: TimelineProps) {
  return (
    <ol aria-label={label} className={cn("flex flex-col", className)}>
      {children}
    </ol>
  );
}

export type TimelineItemProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  status?: TimelineStatus;
  /** Rendered as supporting metadata, e.g. a formatted date. */
  timestamp?: React.ReactNode;
  /** A button or link that advances or opens the milestone. */
  action?: React.ReactNode;
  /** Suppresses the connector on the final milestone. */
  last?: boolean;
  className?: string;
};

/**
 * One milestone. The status is spoken as text as well as shown as a coloured
 * marker, so progress is never carried by colour alone.
 */
export function TimelineItem({
  title,
  description,
  status = "upcoming",
  timestamp,
  action,
  last = false,
  className,
}: TimelineItemProps) {
  const meta = statusMeta[status];
  const Icon = meta.icon;

  return (
    <li
      aria-current={status === "current" ? "step" : undefined}
      className={cn("relative flex gap-3 pb-5 last:pb-0", className)}
    >
      {!last && (
        <span
          aria-hidden="true"
          className="absolute left-[0.9375rem] top-8 bottom-0 w-px bg-border"
        />
      )}

      <span
        className={cn(
          "relative z-10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border",
          markerStyles[meta.tone],
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {title}
            <span className="sr-only">{` — ${meta.label}`}</span>
          </p>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          {timestamp && (
            <p className="mt-1 text-2xs text-muted-foreground">{timestamp}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </li>
  );
}
